import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAccount } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { Bars, Oval } from "react-loader-spinner";
import {
  BsPeople,
  BsCollection,
  BsShare,
  BsDiscord,
  BsTelegram,
  BsTwitter,
  BsGlobe,
  BsLink45Deg,
  BsCalendar,
  BsCheckCircle,
  BsShield,
  BsAward,
  BsArrowLeft,
  BsChevronDown,
  BsChevronUp,
} from "react-icons/bs";
import { SiNotion } from "react-icons/si";
import { HiOutlineSparkles } from "react-icons/hi";
import { MdOutlineModeEditOutline, MdVerified, MdAdminPanelSettings } from "react-icons/md";

import Container from "../components/common/Container";
import MetaTags from "../components/common/MetaTags";
import notify from "../components/common/notify";
import ShareModal from "../components/common/ShareModal";
import FullScreenImage from "../components/common/FullscreenImage";
import UserPlaceHolderImg from "../assets/img/common/user-placeholder-img.png";
import WalletDisplay from "../components/common/WalletDisplay";
import JoinCommunityModal from "../components/communities/JoinCommunityModal";
import PremiumContentSection from "../components/communities/PremiumContentSection";
import AdminManagement from "../components/communities/AdminManagement";
import LikeButton from "../components/common/LikeButton";
import { formatWalletAddress } from "../utils/walletDisplay";

import {
  useGetCommunityByIdQuery,
  useGetCommunityMembersQuery,
  useLikeCommunityMutation,
  useDislikeCommunityMutation,
  useSyncMembershipFromChainMutation,
} from "../redux/slices/api/communityApiSlice";
import { useGetUserQuery } from "../redux/slices/api/userApi";
import { selectGeneral, setShowAuthModal, setShowFullscreenImage } from "../redux/slices/generalSlice";
import { getChain, getNetworkCurrency, getNativeCurrencyTicker, contractNetworks } from "../utils/currencies";
import useJoinCommunity from "../hooks/useJoinCommunity";

// Category labels
const categoryLabels = {
  desci_general: "DeSci General",
  biotech_life_sciences: "Biotech & Life Sciences",
  ai_computer_science: "AI & Computer Science",
  open_source_software: "Open Source Software",
  women_in_science: "Women in Science",
  regional: "Regional",
  climate_environment: "Climate & Environment",
  space_astronomy: "Space & Astronomy",
  other: "Other",
};

// Tier colors
const tierGradients = {
  bronze: "from-amber-600 to-amber-400",
  silver: "from-gray-400 to-gray-300",
  gold: "from-yellow-500 to-yellow-300",
  platinum: "from-slate-600 to-slate-400",
  custom: "from-purple-600 to-purple-400",
};

// Perk labels
const perkLabels = {
  privateChannels: { label: "Private Channels", icon: "🔒" },
  priorityInvestment: { label: "Priority Investment", icon: "💰" },
  votingRights: { label: "Voting Rights", icon: "🗳️" },
  exclusiveEvents: { label: "Exclusive Events", icon: "🎟️" },
  futureAirdrops: { label: "Future Airdrops", icon: "🎁" },
  earlyResearchAccess: { label: "Early Research Access", icon: "📚" },
  specialRole: { label: "Special Role", icon: "⭐" },
  merchandise: { label: "Merchandise", icon: "👕" },
  partnerDiscounts: { label: "Partner Discounts", icon: "💎" },
};

// Link icons
const linkIcons = {
  discord: BsDiscord,
  telegram: BsTelegram,
  twitter: BsTwitter,
  website: BsGlobe,
  forum: BsLink45Deg,
  notion: SiNotion,
  calendar: BsCalendar,
};

const linkColors = {
  discord: "text-indigo-500 bg-indigo-50 hover:bg-indigo-100",
  telegram: "text-blue-500 bg-blue-50 hover:bg-blue-100",
  twitter: "text-sky-500 bg-sky-50 hover:bg-sky-100",
  website: "text-green-500 bg-green-50 hover:bg-green-100",
  forum: "text-orange-500 bg-orange-50 hover:bg-orange-100",
  notion: "text-gray-700 bg-gray-100 hover:bg-gray-200",
  calendar: "text-red-500 bg-red-50 hover:bg-red-100",
};

const CommunityView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { address, isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { isLoggedIn, showFullscreenImage } = useSelector(selectGeneral);

  const [expandedMembers, setExpandedMembers] = useState(false);
  const [membersPage, setMembersPage] = useState(1);
  const [allMembers, setAllMembers] = useState([]);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [waitingForWalletConnection, setWaitingForWalletConnection] = useState(false);
  const [isSyncingMembership, setIsSyncingMembership] = useState(false);
  const syncAttemptedRef = useRef(false);

  // API queries - pass wallet address for anonymous wallet users
  const { data: community, isLoading, error, refetch: refetchCommunity } = useGetCommunityByIdQuery({ 
    id,
    walletAddress: isConnected && address ? address : undefined,
  });
  const { data: membersData, refetch: refetchMembers, isFetching: isFetchingMembers } = useGetCommunityMembersQuery(
    { id, page: membersPage, limit: 20 },
    { skip: !community }
  );

  // Accumulate members across pages for "load more" functionality.
  // Only depend on membersData so the effect runs once per API response,
  // not twice (once when membersPage increments and again when data arrives).
  // Use the page number echoed back by the API to decide replace vs append.
  useEffect(() => {
    if (membersData?.members) {
      const isFirstPage = (membersData.pagination?.page ?? 1) === 1;
      setAllMembers((prev) =>
        isFirstPage ? membersData.members : [...prev, ...membersData.members]
      );
    }
  }, [membersData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset members when community changes
  useEffect(() => {
    setMembersPage(1);
    setAllMembers([]);
  }, [id]);
  const { data: userData } = useGetUserQuery(undefined, { skip: !isLoggedIn });

  const [syncMembershipFromChain] = useSyncMembershipFromChainMutation();
  
  // Check if user has on-chain NFT but isn't shown as member - auto sync if needed
  useEffect(() => {
    const checkAndSyncMembership = async () => {
      // Only check once per page load
      if (syncAttemptedRef.current) return;
      
      // Only check if wallet is connected and we have community data
      if (!isConnected || !address || !community) return;
      
      // Don't check if already shown as member
      if (community.isMember) return;
      
      // Don't check if no contract deployed
      const contractAddress = community.nftDetails?.contractAddress;
      if (!contractAddress || contractAddress === "0x" + "0".repeat(40)) return;
      
      syncAttemptedRef.current = true;
      
      try {
        // Let the backend verify ownership directly from chain and sync if needed.
        setIsSyncingMembership(true);

        const result = await syncMembershipFromChain({
          id,
          body: { walletAddress: address },
        }).unwrap();

        if (result.synced) {
          notify("success", "Your membership has been synced from the blockchain! 🎉");
          refetchCommunity();
          refetchMembers();
        }
      } catch (err) {
        console.warn("Error checking on-chain membership:", err);
        // Don't show error to user - this is a background check
      } finally {
        setIsSyncingMembership(false);
      }
    };
    
    checkAndSyncMembership();
  }, [isConnected, address, community, id, syncMembershipFromChain, refetchCommunity, refetchMembers]);
  
  // Reset sync attempt when community changes
  useEffect(() => {
    syncAttemptedRef.current = false;
  }, [id]);

  // Join community hook with proper wallet/network/transaction flow
  const {
    joinStatus,
    isJoining,
    currentStep,
    txHash,
    error: joinError,
    canJoin,
    isFreePremint,
    joinCommunity,
    resetStatus,
  } = useJoinCommunity(community, {
    onSuccess: useCallback((result) => {
      setShowJoinModal(false);
      refetchCommunity();
      refetchMembers();
      // Optional: navigate to a success page or show additional info
    }, [refetchCommunity, refetchMembers]),
    onError: useCallback((error) => {
      // Error is already handled by the hook, just log for debugging
      console.error("Join error:", error);
      
      // If wallet is not connected, close modal
      if (error?.message === "Wallet not connected") {
        setShowJoinModal(false);
        // resetStatus will be called inline if needed
      }
    }, []),
  });

  // Derive progress (0-100) from joinStatus step completions
  const progress = useMemo(() => {
    const steps = Object.values(joinStatus).filter((s) => !s.hidden);
    if (steps.length === 0) return 0;
    const completed = steps.filter((s) => s.status === "success").length;
    return Math.round((completed / steps.length) * 100);
  }, [joinStatus]);

  // Callback to refetch community after like/dislike
  const onToggleLike = async () => {
    try {
      await refetchCommunity();
    } catch (error) {
      console.error("Failed to refetch community:", error);
    }
  };

  // Handle share - generate social media share URLs
  const getShareUrl = () => typeof window !== 'undefined' ? window.location.href : '';
  
  const getShareData = () => ({
    id: id, // Community ID for badge generation
    title: community?.name || 'Community',
    description: community?.shortDescription || community?.description || '',
    image: community?.artwork || community?.bannerImage || null,
    creator: community?.creator?.name || community?.creator?.email || 'Anonymous',
    extra: {
      price: community?.saleSettings?.price || 0,
      currency: displayCurrency,
      members: community?.stats?.members || 0,
    },
  });

  // Handle join/purchase - complete flow with wallet, network, payment, and minting
  const handleJoin = async () => {
    // Check if wallet is connected first
    if (!isConnected) {
      // Mark that we're waiting for wallet connection
      setWaitingForWalletConnection(true);
      // Open wallet connect modal and wait for user action
      openConnectModal?.();
      // Don't proceed with join flow yet - let user connect first
      // The useEffect below will auto-start the flow once connected
      return;
    }
    
    // Wallet is connected, proceed with join flow
    setWaitingForWalletConnection(false);
    setShowJoinModal(true);
    await joinCommunity();
  };
  
  // Auto-start join flow when wallet connects (if user was trying to join)
  useEffect(() => {
    if (waitingForWalletConnection && isConnected) {
      setWaitingForWalletConnection(false);
      setShowJoinModal(true);
      joinCommunity();
    }
  }, [waitingForWalletConnection, isConnected, joinCommunity]);
  
  // Reset waiting state after timeout (user likely cancelled wallet connection)
  useEffect(() => {
    if (waitingForWalletConnection) {
      const timeout = setTimeout(() => {
        setWaitingForWalletConnection(false);
      }, 60000); // 60 seconds timeout
      
      return () => clearTimeout(timeout);
    }
  }, [waitingForWalletConnection]);

  // Handle retry from modal
  const handleRetryJoin = () => {
    resetStatus();
    joinCommunity();
  };

  // Handle modal close
  const handleCloseJoinModal = () => {
    if (!isJoining) {
      setShowJoinModal(false);
      resetStatus();
      // If join was successful, refetch data
      if (progress === 100) {
        refetchCommunity();
        refetchMembers();
      }
    }
  };

  if (isLoading) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center">
        <Bars height="50" width="50" color="#8538ff" visible={true} />
      </div>
    );
  }

  if (error || !community) {
    return (
      <div className="w-full min-h-screen flex flex-col justify-center items-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Community not found</h2>
        <Link
          to="/communities"
          className="text-primary hover:underline flex items-center gap-2"
        >
          <BsArrowLeft />
          Back to Communities
        </Link>
      </div>
    );
  }

  const effectiveNetwork = community.deploymentTx?.network || community.blockchain;
  const chainInfo = getChain({ network: effectiveNetwork });
  const displayCurrency =
    getNetworkCurrency(effectiveNetwork, community.saleSettings?.currency)
      ? community.saleSettings?.currency
      : getNativeCurrencyTicker(effectiveNetwork);
  const availableNFTs = community.stats?.available || 0;
  const isSoldOut = availableNFTs === 0;
  const isMember = community.isMember;
  const isCreator = community.isCreator;
  const isAdmin = community.isAdmin;

  // Get active perks
  const activeChecklistPerks = Object.entries(community.perkChecklist || {})
    .filter(([_, active]) => active)
    .map(([key]) => ({ key, ...perkLabels[key] }));

  const allPerks = [
    ...activeChecklistPerks,
    ...(community.perks || []).map((p) => ({ key: p._id, label: p.title, icon: "✨" })),
  ];

  // Get community links
  const communityLinks = Object.entries(community.links || {}).filter(
    ([_, url]) => url && url.trim() !== ""
  );

  return (
    <div className="w-full min-h-screen bg-gray-50 roboto-font">
      <MetaTags
        title={community.name}
        description={community.shortDescription}
        link={`/communities/${id}`}
      />

      {/* Banner */}
      <div className="relative h-64 md:h-80 bg-gradient-to-r from-purple-600 to-blue-600">
        {community.bannerImage && (
          <img
            src={community.bannerImage}
            alt="Banner"
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/20" />

        {/* Back Button */}
        <div className="absolute top-24 left-6">
          <button
            onClick={() => navigate("/communities")}
            className="flex items-center gap-2 text-white bg-black/30 px-4 py-2 rounded-full hover:bg-black/50 transition-colors"
          >
            <BsArrowLeft />
            Back
          </button>
        </div>

        {/* Action Buttons */}
        <div className="absolute top-24 right-6 flex gap-3">
          {isCreator && (
            <Link
              to={`/create-community?edit=${id}`}
              className="flex items-center gap-2 text-white bg-black/30 px-4 py-2 rounded-full hover:bg-black/50 transition-colors"
            >
              <MdOutlineModeEditOutline />
              Edit
            </Link>
          )}
          {isCreator && (
            <button
              onClick={() => setShowAdminModal(true)}
              className="flex items-center gap-2 text-white bg-black/30 px-4 py-2 rounded-full hover:bg-black/50 transition-colors"
            >
              <MdAdminPanelSettings />
              Manage Admins
            </button>
          )}
          <button
            onClick={() => setShowShareMenu(true)}
            className="flex items-center gap-2 text-white bg-black/30 px-4 py-2 rounded-full hover:bg-black/50 transition-colors"
          >
            <BsShare />
            Share
          </button>
          <div className="bg-black/30 px-2 py-1 rounded-full hover:bg-black/50 transition-colors">
            <LikeButton
              likes={community?.likes || 0}
              useLikeMutation={useLikeCommunityMutation}
              useDislikeMutation={useDislikeCommunityMutation}
              id={id}
              likeType="community"
              onToggleLike={onToggleLike}
              containerClass="w-8 h-10"
              heartClass="w-16 h-16"
              countClass="text-sm -mt-5 text-white"
            />
          </div>
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        show={showShareMenu}
        setShow={setShowShareMenu}
        type="community"
        data={getShareData()}
      />

      {/* Admin Management Modal */}
      {showAdminModal && (
        <AdminManagement
          communityId={id}
          onClose={() => setShowAdminModal(false)}
        />
      )}

      <Container className="relative -mt-24 pb-20">
        {/* Batch mint incomplete banner — shown to creator when NFTs need to be minted */}
        {isCreator && community.preMintMode && community.state === "pending_mint" && (
          <div className="mb-6 bg-amber-50 border border-amber-300 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 shadow-sm">
            <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 text-xl">
              ⚠️
            </div>
            <div className="flex-1">
              <p className="font-semibold text-amber-900">Setup Incomplete — NFTs Not Yet Minted</p>
              <p className="text-sm text-amber-700 mt-0.5">
                Your community contract is deployed but the pre-mint step was not completed. Members cannot join until you mint the NFTs. Open this community in edit mode and click "Publish" to retry the minting transaction.
              </p>
            </div>
            <Link
              to={`/create-community?edit=${id}`}
              className="flex-shrink-0 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Complete Setup →
            </Link>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {/* Community Card */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              {/* Header with artwork */}
              <div className="p-6 md:p-8 flex flex-col md:flex-row gap-6">
                {/* Artwork */}
                <div 
                  onClick={() => dispatch(setShowFullscreenImage(true))}
                  className="w-40 h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden flex-shrink-0 shadow-lg mx-auto md:mx-0 cursor-pointer hover:shadow-2xl transition-shadow"
                >
                  <img
                    src={community.artwork || "/placeholder.png"}
                    alt={community.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 text-center md:text-left">
                  {/* Badges */}
                  <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-3">
                    {community.membershipTier && (
                      <span
                        className={`px-3 py-1 rounded-full text-xs text-white font-medium bg-gradient-to-r ${
                          tierGradients[community.membershipTier]
                        } capitalize`}
                      >
                        {community.membershipTier} Tier
                      </span>
                    )}
                    {community.featured && (
                      <span className="px-3 py-1 rounded-full text-xs text-white font-medium bg-gradient-to-r from-purple-600 to-pink-500 flex items-center gap-1">
                        <HiOutlineSparkles />
                        Featured
                      </span>
                    )}
                    {chainInfo && (
                      <span className="px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-700 flex items-center gap-1">
                        {chainInfo.icon && (
                          <img src={chainInfo.icon} alt="" className="w-4 h-4" />
                        )}
                        {chainInfo.name}
                      </span>
                    )}
                  </div>

                  {/* Name & Category */}
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                    {community.name}
                  </h1>
                  <p className="text-gray-500 mb-3">
                    {categoryLabels[community.category] || community.category}
                  </p>

                  {/* Short Description */}
                  <p className="text-gray-600">{community.shortDescription}</p>

                  {/* Creator */}
                  <Link
                    to={`/profile?id=${community.creator?._id}`}
                    className="inline-flex items-center gap-3 mt-4 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <img
                      src={community.creator?.images?.profile || UserPlaceHolderImg}
                      alt={community.creator?.fundername}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="text-left">
                      <p className="text-xs text-gray-500">Created by</p>
                      <p className="font-medium text-gray-800 flex items-center gap-1">
                        {community.creator?.fundername ||
                          `${community.creator?.firstname || ""} ${community.creator?.lastname || ""}`.trim() ||
                          "Creator"}
                        <MdVerified className="text-primary text-sm" />
                      </p>
                      {community.creator?.title_rank && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {community.creator.title_rank}
                        </p>
                      )}
                    </div>
                  </Link>
                </div>
              </div>

              {/* Stats Bar */}
              <div className="mx-6 my-4 grid grid-cols-4 gap-4 p-4 bg-white/60 backdrop-blur-xl rounded-2xl border border-white/20 shadow-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">
                    {community.stats?.totalSupply || 0}
                  </p>
                  <p className="text-xs text-gray-500">Total Supply</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-500">
                    {community.stats?.sold || 0}
                  </p>
                  <p className="text-xs text-gray-500">Sold</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-500">
                    {community.stats?.members || 0}
                  </p>
                  <p className="text-xs text-gray-500">Members</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-800">
                    {community.views || 0}
                  </p>
                  <p className="text-xs text-gray-500">Views</p>
                </div>
              </div>

              {/* Community Links */}
              {communityLinks.length > 0 && (
                <div className="mx-6 my-4 p-5 bg-white/60 backdrop-blur-xl rounded-2xl border border-white/20 shadow-lg">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <BsLink45Deg className="text-primary" />
                    Community Links
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {communityLinks.map(([key, url]) => {
                      const Icon = linkIcons[key] || BsLink45Deg;
                      return (
                        <a
                          key={key}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-colors ${
                            linkColors[key] || "text-gray-600 bg-gray-100 hover:bg-gray-200"
                          }`}
                        >
                          <Icon className="text-lg" />
                          <span className="capitalize font-medium">{key}</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Long Description */}
              {community.longDescription && (
                <div className="mx-6 my-4 p-5 bg-white/60 backdrop-blur-xl rounded-2xl border border-white/20 shadow-lg">
                  <h3 className="font-semibold text-gray-800 mb-3">About</h3>
                  <div
                    className="prose prose-sm max-w-none text-gray-600"
                    dangerouslySetInnerHTML={{ __html: community.longDescription }}
                  />
                </div>
              )}

              {/* Perks */}
              {allPerks.length > 0 && (
                <div className="mx-6 my-4 p-5 bg-white/60 backdrop-blur-xl rounded-2xl border border-white/20 shadow-lg">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <BsAward className="text-primary" />
                    Member Perks ({allPerks.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {allPerks.map((perk, i) => (
                      <div
                        key={perk.key || i}
                        className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl"
                      >
                        <span className="text-xl">{perk.icon}</span>
                        <span className="font-medium text-gray-800">{perk.label}</span>
                        <BsCheckCircle className="text-green-500 ml-auto" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Members - Collapsible */}
              {(allMembers.length > 0 || membersData?.pagination?.total > 0) && (
                <div className="mx-6 my-4 bg-white/60 backdrop-blur-xl rounded-2xl border border-white/20 shadow-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedMembers(!expandedMembers)}
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/40 transition-colors"
                  >
                    <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                      <BsPeople className="text-primary" />
                      Members ({community.stats?.members || 0})
                    </h3>
                    {expandedMembers ? (
                      <BsChevronUp className="text-gray-500" />
                    ) : (
                      <BsChevronDown className="text-gray-500" />
                    )}
                  </button>

                  {!expandedMembers ? (
                    // Collapsed: avatar stack
                    <div className="px-5 pb-4">
                      <div className="flex flex-wrap gap-2">
                        {allMembers.slice(0, 12).map((member) => (
                          <Link
                            key={member._id || member.walletAddress}
                            to={member.user && !member.isAnonymous ? `/profile?id=${member.user._id}` : "#"}
                            className="group"
                          >
                            <img
                              src={member.user?.images?.profile || UserPlaceHolderImg}
                              alt=""
                              className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm group-hover:scale-110 transition-transform"
                              title={member.displayName || formatWalletAddress(member.walletAddress)}
                            />
                          </Link>
                        ))}
                        {(community.stats?.members || 0) > 12 && (
                          <button
                            onClick={() => setExpandedMembers(true)}
                            className="w-10 h-10 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center hover:bg-primary/20 transition-colors"
                          >
                            +{(community.stats?.members || 0) - 12}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Expanded: full member list with load more
                    <div className="px-5 pb-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {allMembers.map((member) => {
                          const explorerUrl = contractNetworks[effectiveNetwork]?.explorer;
                          const profileLink = member.user && !member.isAnonymous ? `/profile?id=${member.user._id}` : null;
                          return (
                            <div
                              key={member._id || member.walletAddress}
                              className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:border-primary/30 hover:shadow-md transition-all"
                            >
                              {profileLink ? (
                                <Link to={profileLink} className="flex-shrink-0">
                                  <img
                                    src={member.user?.images?.profile || UserPlaceHolderImg}
                                    alt=""
                                    className="w-11 h-11 rounded-full object-cover border-2 border-white shadow hover:scale-110 transition-transform"
                                  />
                                </Link>
                              ) : (
                                <img
                                  src={UserPlaceHolderImg}
                                  alt=""
                                  className="w-11 h-11 rounded-full object-cover border-2 border-white shadow opacity-50 flex-shrink-0"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                {profileLink ? (
                                  <Link
                                    to={profileLink}
                                    className="font-medium text-gray-800 hover:text-primary transition-colors block truncate text-sm"
                                  >
                                    {member.displayName}
                                  </Link>
                                ) : (
                                  <WalletDisplay
                                    address={member.walletAddress}
                                    fallback={member.displayName}
                                    className="font-medium text-gray-400 text-sm"
                                  />
                                )}
                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                  {member.tokenId !== undefined && member.tokenId !== null && (
                                    <span className="text-xs text-gray-400 flex items-center gap-1">
                                      <BsCollection className="text-primary/60" />
                                      #{member.tokenId}
                                    </span>
                                  )}
                                  {member.joinedAt && (
                                    <span className="text-xs text-gray-400">
                                      {new Date(member.joinedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                    </span>
                                  )}
                                  {member.tokenId !== undefined && member.tokenId !== null && community.nftDetails?.contractAddress && explorerUrl && (
                                    <a
                                      href={`${explorerUrl}/token/${community.nftDetails.contractAddress}?a=${member.tokenId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-primary hover:underline flex items-center gap-0.5"
                                      title="View NFT"
                                    >
                                      <BsShield className="text-xs" />
                                      NFT
                                    </a>
                                  )}
                                  {member.txHash && explorerUrl && (
                                    <a
                                      href={`${explorerUrl}/tx/${member.txHash}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-500 hover:underline flex items-center gap-0.5"
                                      title="View TX"
                                    >
                                      <BsCheckCircle className="text-xs" />
                                      TX
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Load more */}
                      {membersData?.pagination && membersPage < membersData.pagination.pages && (
                        <button
                          onClick={() => setMembersPage((p) => p + 1)}
                          disabled={isFetchingMembers}
                          className="mt-4 w-full py-2.5 rounded-xl bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {isFetchingMembers ? (
                            <Oval height={16} width={16} color="#8538ff" secondaryColor="#c4a0ff" strokeWidth={3} />
                          ) : (
                            <>
                              <BsChevronDown />
                              Load more ({membersData.pagination.total - allMembers.length} remaining)
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Premium Content Section - Token Gated */}
              <div className="mx-6 mb-6">
                <PremiumContentSection
                  community={community}
                  isMember={isMember}
                  isCreator={isCreator}
                  isAdmin={isAdmin}
                  onJoinClick={handleJoin}
                  currentUserId={userData?._id}
                />
              </div>
            </div>
          </div>

          {/* Sidebar - Purchase Card */}
          <div className="w-full lg:w-96">
            <div className="bg-white rounded-3xl shadow-xl p-6 sticky top-28">
              {/* Price */}
              <div className="text-center mb-6">
                <p className="text-sm text-gray-500 mb-1">
                  {community.saleSettings?.type === "fixed"
                    ? "Price"
                    : "Starting at"}
                </p>
                {isFreePremint || (Number(community.saleSettings?.price) || 0) === 0 ? (
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-4xl font-bold bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent">
                      FREE
                    </span>
                    {isFreePremint && (
                      <span className="text-xs bg-green-100 text-green-700 font-medium px-3 py-1 rounded-full">
                        FunDeSci profile required
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-4xl font-bold text-gray-800">
                      {community.saleSettings?.price}
                    </span>
                    <span className="text-xl text-gray-500">
                      {displayCurrency}
                    </span>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1 capitalize">
                  {community.membershipDuration || "Lifetime"} membership
                </p>
              </div>

              {/* Blockchain Network */}
              {effectiveNetwork && (
                <div className="flex flex-col gap-2 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl mb-6">
                  <div className="flex items-center justify-center gap-2">
                    {chainInfo?.icon && (
                      <img
                        src={chainInfo.icon}
                        alt={chainInfo.name}
                        className="w-5 h-5"
                      />
                    )}
                    <span className="text-sm font-medium text-gray-700">
                      Published on {chainInfo?.name || effectiveNetwork}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-1 text-xs text-gray-500">
                    {isFreePremint || (Number(community.saleSettings?.price) || 0) === 0 ? (
                      <span>Mint on {chainInfo?.name || effectiveNetwork}</span>
                    ) : (
                      <>
                        <span>Pay with</span>
                        <span className="font-semibold text-primary">{displayCurrency}</span>
                        <span>on {chainInfo?.name || effectiveNetwork}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Availability */}
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl mb-6">
                <div className="flex items-center gap-2 text-gray-600">
                  <BsCollection />
                  <span>Available</span>
                </div>
                <span className="font-bold text-gray-800">
                  {availableNFTs} / {community.stats?.totalSupply}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-600 to-blue-500 transition-all"
                    style={{
                      width: `${
                        ((community.stats?.sold || 0) /
                          (community.stats?.totalSupply || 1)) *
                        100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{community.stats?.sold || 0} sold</span>
                  <span>
                    {Math.round(
                      ((community.stats?.sold || 0) /
                        (community.stats?.totalSupply || 1)) *
                        100
                    )}
                    %
                  </span>
                </div>
              </div>

              {/* CTA Button */}
              {isMember ? (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-100 text-green-700 rounded-xl font-medium">
                    <BsCheckCircle />
                    You're a Member
                  </div>
                  <p className="text-sm text-gray-500 mt-3">
                    Access your perks in the community links above
                  </p>
                </div>
              ) : !canJoin?.allowed ? (
                <div className="text-center">
                  <button
                    disabled
                    className="w-full py-4 bg-gray-200 text-gray-500 rounded-xl font-medium cursor-not-allowed"
                  >
                    {canJoin?.reason || "Cannot Join"}
                  </button>
                </div>
              ) : isSoldOut ? (
                <button
                  disabled
                  className="w-full py-4 bg-gray-200 text-gray-500 rounded-xl font-medium cursor-not-allowed"
                >
                  Sold Out
                </button>
              ) : (
                <>
                  <button
                    onClick={handleJoin}
                    disabled={isJoining}
                    className={`w-full py-4 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2 ${
                      isFreePremint
                        ? "bg-gradient-to-r from-green-500 to-emerald-500"
                        : "bg-gradient-to-r from-[#8538ff] to-[#7e7cff]"
                    }`}
                  >
                    {isJoining ? (
                      <>
                        <Oval height={20} width={20} color="#fff" secondaryColor="#fff" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <HiOutlineSparkles />
                        {isFreePremint ? "Claim Free Membership" : "Join Community"}
                      </>
                    )}
                  </button>
                  {isFreePremint ? (
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Free for users with a FunDeSci profile &mdash; one per account + gas fees
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 text-center mt-2">
                      {community.saleSettings?.price || 0} {displayCurrency} + gas fees
                    </p>
                  )}
                </>
              )}

              {/* Trust Indicators */}
              <div className="mt-6 pt-6 border-t border-gray-100 space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <BsShield className="text-green-500" />
                  <span>Verified on-chain membership</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <BsCheckCircle className="text-green-500" />
                  <span>{community.royalties || 5}% creator royalties</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>

      {/* Join Community Modal */}
      <JoinCommunityModal
        isOpen={showJoinModal}
        onClose={handleCloseJoinModal}
        community={community}
        joinStatus={joinStatus}
        progress={progress}
        currentStep={currentStep}
        error={joinError}
        txHash={txHash}
        isJoining={isJoining}
        onRetry={handleRetryJoin}
      />

      {/* Fullscreen Image Modal */}
      {showFullscreenImage && (
        <FullScreenImage 
          img={community?.artwork || "/placeholder.png"}
        />
      )}
    </div>
  );
};

export default CommunityView;
